import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { useRest } from "@/composables/index";
import { useStoreCompat } from "@/composables";


const emptyForm = () => ({
    PersonalIntervinienteID: null,
    nombreyapellido: "",
    jerarquia: "",
    dependencia: ""
});

export const usePersonalIntervinienteStore = defineStore("personalInterviniente", () => {
    const store = useStoreCompat();
    const selected = ref(null);
    const form = ref(emptyForm());
    const formType = ref("view"); // estados del formulario view, edit, create
    const intervinientesList = ref([]);
    const actuacionID = ref("");
    const rest = useRest();
    const isLoading = ref(false);
    const baseUrl = `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/personal-interviniente`;
    
    const fetchIntervinientes = async (id, indexToRedirect) => {
        actuacionID.value = id;
        try {
          const url = `${baseUrl}/?search={"ActuacionID":[{"operator":"=","value":"${id}"}]}`;
          const resp = await rest.get(url);
          intervinientesList.value = resp.data.data;
          if (indexToRedirect !== undefined) {
            selectNextOrPrevious(indexToRedirect);
          } else {
            selectFirst();
          }
        } catch (e) {
          console.error("Error al traer Personal Interviniente", e);
        }
      };
    
      const validateForm = () => {
        const camposObligatorios = ["nombreyapellido", "jerarquia", "dependencia"];
      
        const invalidField = camposObligatorios.find(
          (campo) => !form.value?.[campo]
        );
      
        isFormValid.value = !invalidField;
        return isFormValid.value;
      };      
    
      const buildPayload = () => ({
        ActuacionID: actuacionID.value,
        Nombreyapellido: form.value.nombreyapellido,
        Jerarquia: form.value.jerarquia?.nombre || "",
        Dependencia: form.value.dependencia?.nombre || "",
      });

      const handlePutIntervinientes = async () => {
        if (!validateForm()) return;
        isLoading.value = true;

        try {
          const payload = buildPayload();

          if (isCreate.value) {
            await rest.post({
              url: `${baseUrl}`,
              formData: payload,
              ContentType: "application/x-www-form-urlencoded",
            });
          } else if (isEdit.value) {
            const id = form.value.PersonalIntervinienteID;
            if (!id) throw new Error("Falta PersonalIntervinienteID para editar");

            await rest.put({
              url: `${baseUrl}/${id}`,
              formData: payload,
              ContentType: "application/x-www-form-urlencoded",
            });
          }

          await fetchIntervinientes(actuacionID.value);
          startView();
          triggerSuccessModal();
        } catch (e) {
          console.error("Error al guardar interviniente", e);
          return false;
        } finally {
          isLoading.value = false;
        }
      };
      
      const handleSaveLocally = async () => {
        if (!validateForm()) return;
        isLoading.value = true;
      
        const formatedinterviniente = {
          ActuacionID: "",
          PersonalIntervinienteID: form.value?.PersonalIntervinienteID || `temp-${Date.now()}`,
          Nombreyapellido: form.value.nombreyapellido,
          Jerarquia: form.value.jerarquia?.nombre || "",
          JerarquiaID: form.value.jerarquia?.id || "",
          Dependencia: form.value.dependencia?.nombre || "",
          DependenciaID: form.value.dependencia?.id || "",
        };
        
        if (isCreate.value) {
          intervinientesList.value.push(formatedinterviniente);
        } else if (isEdit.value) {
          const index = intervinientesList.value.findIndex(
            (v) => v.PersonalIntervinienteID === form.value.PersonalIntervinienteID
          );
          if (index !== -1) {
            intervinientesList.value[index] = formatedinterviniente;
          }
        }
      
        isLoading.value = false;
        startView();
      };
      
      const requestDeleteInterviniente = async (id) => {
       await rest.del({
          url: `${baseUrl}/${id}`,
          formData: {},
          ContentType: "application/x-www-form-urlencoded",
        });
      };
      
      const handleDeleteInterviniente = async () => {
        const id = selectedId.value;
        if (!id) return;

        isLoading.value = true;

        try {
          if (String(id).startsWith("temp-")) {
            intervinientesList.value = intervinientesList.value.filter(
              (item) => item.PersonalIntervinienteID !== id
            );
          } else {
            await requestDeleteInterviniente(id);
          }

          if (actuacionID.value) {
            await fetchIntervinientes(actuacionID.value);
          }

          startView();
          return true;
        } catch (error) {
          console.error("Error al eliminar interviniente", error);
          return false;
        } finally {
          isLoading.value = false;
        }
      };
    
      const handleCreateInterviniente = async (newInterviniente) => {
        intervinientesList.value = [
          ...intervinientesList.value,
          {
            ...newInterviniente,
          },
        ];
        await handleSave();
      };
    
      const handleSave = async (indexToRedirect) => {
        try {
          const formattedIntervinienteList = intervinientesList.value.map((interviniente) => ({
            ...interviniente,
          }));
    
          let options = {
            url: `${baseUrl}/${actuacionID.value}`,                     
            formData: { Intervinientes: formattedIntervinienteList },
            ContentType: "text/plain",
          };
          await rest.put(options);
        } catch (error) {
          console.error("Error al editar intervninientes", error);
        } finally {
          isLoading.value = false;
          if (isCreate.value || isEdit.value) triggerSuccessModal();
          startView();
          if (indexToRedirect !== undefined) clear();
          fetchIntervinientes(actuacionID.value, indexToRedirect);
        }
      };
      
      const select = (interviniente) => {
        selected.value = interviniente;
        hydrateFormFromDoc(interviniente);
        formType.value = "view";
      };
    
      const startCreate = () => {
        selected.value = null;
        form.value = emptyForm();
        formType.value = "create";
      };
    
      const hydrateFormFromDoc = (interviniente) => {
        const jerarquiaObj = store.state.siis.jerarquia.find(
          (j) => j.nombre === interviniente?.Jerarquia
        );
        const dependenciaObj = store.getters["siis/getDependecias"]
        .map(d => ({ id: d.id, nombre: d.dependencia }))
        .find(d => d.nombre === interviniente?.Dependencia);
      
        form.value = {
          PersonalIntervinienteID: interviniente.PersonalIntervinienteID,
          nombreyapellido: interviniente?.Nombreyapellido || "",
          jerarquia: jerarquiaObj || { nombre: interviniente?.Jerarquia || "" },
          dependencia: dependenciaObj || { nombre: interviniente?.Dependencia || "" },
        };
      };
      
      const startEdit = () => {
        formType.value = "edit";
      };
      
      const startView = () => {
        formType.value = "view";
      };
    
      const resetForm = () => {
        form.value = emptyForm();
      };
    
      const clear = () => {
        selected.value = null;
        resetForm();
      };
    
      const selectFirst = () => {
        if (intervinientesList.value?.length) {
          select(intervinientesList.value[0]);
        }
      };
      
      const selectNextOrPrevious = (indexToRedirect) => {
        if (intervinientesList.value[indexToRedirect] === undefined) {
          select(intervinientesList.value[indexToRedirect - 1]);
          return;
        }
        if (intervinientesList.value?.length) {
          select(intervinientesList.value[indexToRedirect]);
        }
      };
      
      const selectedId = computed(() => selected.value?.PersonalIntervinienteID ?? null);
      
      const isSelected = (id) => selected.value?.PersonalIntervinienteID === id;
      const isView = computed(() => formType.value === "view");
      const isEdit = computed(() => formType.value === "edit");
      const isCreate = computed(() => formType.value === "create");
      const inputsDisabled = computed(() => formType.value === "view");
      const isFormValid = ref(false);
      const validationErrors = ref({});
            
      const showSuccessModal = ref();

      const setShowSuccessModal = (fn) => {
        showSuccessModal.value = fn;
      };

      const triggerSuccessModal = () => {
        showSuccessModal.value?.();
      };
      
      const setValidationErrors = (errors) => {
        validationErrors.value = errors;
      };
      
      return {
        // state
        selected,
        form,
        formType,
        intervinientesList,
        isLoading,
    
        // actions
        select,
        hydrateFormFromDoc,
        resetForm,
        clear,
        startCreate,
        startEdit,
        handleSave,
        fetchIntervinientes,
        handlePutIntervinientes,
        handleSaveLocally,
        handleDeleteInterviniente,
        handleCreateInterviniente,
        startView,
        setShowSuccessModal,
        triggerSuccessModal,
        selectFirst,
        validateForm,
        setValidationErrors,
    
        // getters
        selectedId,
        isSelected,
        isCreate,
        isEdit,
        isView,
        inputsDisabled,
        isFormValid,
      };
});